import { useEffect, useRef, useState } from "react";
import { fetchCaseSheetPermissionForUser } from "../api/caseSheetAccessApi";

/**
 * Fetches the case-sheet visibility table (`case_sheet_access/for_user/`).
 * Fails open — `permissionMap` stays `null` until the fetch resolves, and
 * callers should treat a missing/`null` map as "show everything" so Case Sheet
 * behaves exactly as before for any facility that hasn't tagged departments
 * yet, or if the request errors out.
 *
 * `doctorId` is optional: pass it when the viewer is not the doctor themselves
 * (e.g. a facility user opening a doctor's appointment) to get that doctor's
 * department table instead of the caller's own.
 */
export const useCaseSheetPermission = (enabled, doctorId) => {
  const [permissionMap, setPermissionMap] = useState(null);
  const fetchedDoctorIdRef = useRef(doctorId);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Only when we switch doctors: drop the table we already hold, since it
    // describes someone else's department. Callers that never pass a doctorId
    // keep their cached map across reopens, as before.
    if (fetchedDoctorIdRef.current !== doctorId) {
      fetchedDoctorIdRef.current = doctorId;
      setPermissionMap(null);
    }

    fetchCaseSheetPermissionForUser(doctorId)
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data?.case_sheet_permission || []).forEach((item) => {
          map[item.code] = item.is_visible !== false;
        });
        setPermissionMap(map);
      })
      .catch((err) => {
        console.error("Failed to fetch case sheet permission", err);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, doctorId]);

  return { permissionMap };
};

export default useCaseSheetPermission;
